package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.models.AnnuncioStato
import com.heelion.farmafreelance.models.AnnuncioTipo
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.models.Professionista
import com.heelion.farmafreelance.voice.VoiceCommandParser
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/** Criteri di una ricerca "cerco farmacista galenico a 35 € l'ora di sabato a Bergamo" (o "cerco infermiere per medicazioni…"). */
data class CriteriFarmacista(
    /** Chi cerca la farmacia: farmacista (default) o infermiere, se la frase lo nomina. */
    val professione: Professione = Professione.FARMACISTA,
    /** Nome esatto della competenza come nel profilo (es. "Galenica"). */
    val competenza: String? = null,
    /** Tariffa oraria massima che la farmacia vuole pagare. */
    val tariffaMax: Double? = null,
    val giorno: LocalDate? = null,
    val comune: String? = null,
    val provincia: String? = null,
    /** "a novembre", "la prossima settimana", "dal 3 al 10 novembre": al posto di [giorno]. */
    val periodo: PeriodoRicerca? = null,
    /** "in zona", "nella mia zona", "vicino a me": nella zona del profilo della farmacia. */
    val inZona: Boolean = false
) {
    /** Basta almeno uno di questi perché sia una ricerca "intelligente" e non la normale ricerca per zona. */
    val haFiltriFarmy: Boolean get() = competenza != null || tariffaMax != null || giorno != null || periodo != null || inZona

    /** true se quel giorno va bene per la ricerca (nessun giorno o periodo chiesto = ogni giorno). */
    fun accetta(g: LocalDate): Boolean = (giorno == null || g == giorno) && (periodo == null || g in periodo)

    /** "per il 7/11", "a novembre", "dal 3/11 al 10/11"; vuoto se non è stato chiesto un quando. */
    val quando: String
        get() = periodo?.descrizione ?: giorno?.let { "per il ${it.dayOfMonth}/${it.monthNumber}" }.orEmpty()
}

/** Un professionista trovato (farmacista o infermiere); [disponibilita] = suo annuncio "cerco lavoro" per quel giorno, se c'è. */
data class CandidatoFarmacista(
    val farmacista: Professionista,
    val disponibilita: AnnuncioTurno?,
    val preferito: Boolean,
    /** Quanti giorni del periodo chiesto ha già dato disponibilità (entro la tariffa). */
    val giorniDisponibili: Int = if (disponibilita != null) 1 else 0
) {
    val tariffa: Double? get() = disponibilita?.compenso
}

/**
 * Farmy trova il farmacista giusto per una farmacia: la ricerca del server filtra già per
 * competenza, zona e date (farmacisti con disponibilità pubblicata); qui si aggiungono la
 * tariffa (dal compenso orario richiesto negli annunci di disponibilità) e l'ordinamento.
 */
object RicercaFarmacisti {

    // Parole dette -> competenza come salvata nel profilo (ProfileScreen, competenzePredefinite).
    private val COMPETENZE = listOf(
        Regex("galenic|preparazion|laboratorio") to "Galenica",
        Regex("vaccin") to "Vaccinazione",
        Regex("tampon") to "Tamponi",
        Regex("elettrocardiogramm|\\becg\\b") to "Elettrocardiogramma",
        Regex("cosmes|cosmetic|dermocosm") to "Cosmesi",
        Regex("nutrizion|dietet|alimentazion") to "Nutrizione",
        Regex("omeopat") to "Omeopatia",
        Regex("deblister") to "Deblistering",
        Regex("amministrativ|gestional") to "Gestione amministrativa"
    )

    // Le competenze degli infermieri, come nel profilo (competenzeDi nell'app). Le più specifiche prima.
    private val COMPETENZE_INFERMIERE = listOf(
        Regex("medicazion\\w* avanzat|lesion|ulcer|piaghe|wound") to "Medicazioni avanzate",
        Regex("medicazion") to "Medicazioni",
        Regex("iniezion|intramuscol|sottocut|punture") to "Iniezioni",
        Regex("preliev") to "Prelievi capillari",
        Regex("holter pressor|holter della pression") to "Holter pressorio",
        Regex("holter") to "Holter cardiaco",
        Regex("elettrocardiogramm|\\becg\\b") to "Elettrocardiogramma",
        Regex("parametr|pressione|glicemi|saturazion") to "Misurazione parametri",
        Regex("vaccin") to "Vaccinazione",
        Regex("educazion\\w* sanitari") to "Educazione sanitaria",
        Regex("\\bbls|defibrill") to "BLS-D"
    )

    /**
     * "nella mia zona", "vicino a me", "in zona" da solo: la zona del profilo. "In zona Bologna" invece è
     * Bologna: "in zona" vale come "mia zona" solo se dopo non c'è un luogo (fine frase, data, periodo...).
     */
    /** "in zona", "vicino a me"...: si cerca nella zona del profilo (vedi [MIA_ZONA]). */
    fun nellaMiaZona(testo: String): Boolean = MIA_ZONA.containsMatchIn(testo)

    /** [testo] senza "nella mia zona", "vicino a me"...: il parser li leggerebbe come nome di un comune. */
    fun senzaMiaZona(testo: String): String = testo.replace(MIA_ZONA, " ")

    private val MIA_ZONA = Regex(
        "\\b(nella mia zona|in questa zona|qui in zona|vicino a me|vicino a te|qui vicino|nei dintorni|nei paraggi|" +
            "in zona(?=\\s+(?:per|dal|dalla|il|la|lo|le|i|gli|di|del|nel|nella|nei|a|ad|in|questa|questo|prossim\\w*|entro|che|con|" +
            "oggi|domani|dopodomani|luned\\S*|marted\\S*|mercoled\\S*|gioved\\S*|venerd\\S*|sabato|domenica)\\b|\\s*[?.!,]|\\s*$))",
        RegexOption.IGNORE_CASE
    )

    /** La frase cerca un infermiere invece di un farmacista. */
    fun professioneMenzionata(testo: String): Professione =
        if (IntentClassifier.normalizza(testo).contains("infermier")) Professione.INFERMIERE else Professione.FARMACISTA

    /**
     * true se la frase chiede un farmacista con criteri "da Farmy" (competenza, tariffa, giorno):
     * senza, resta la normale ricerca farmacisti per zona dell'app.
     */
    fun eRicercaFarmy(testo: String, oggi: LocalDate): Boolean =
        IntentClassifier.normalizza(testo).let { it.contains("farmacist") || it.contains("infermier") } &&
            // "avvisami quando c'è un infermiere in zona" è un avviso da salvare, non una ricerca adesso.
            !AvvisiFarmy.eRichiestaAvviso(testo) && estrai(testo, oggi).haFiltriFarmy

    /** "galenico" -> "Galenica" (nome della competenza come nel profilo di [professione]); null se non ne nomina. */
    fun competenzaMenzionata(testo: String, professione: Professione = Professione.FARMACISTA): String? {
        val t = IntentClassifier.normalizza(testo)
        val elenco = if (professione == Professione.INFERMIERE) COMPETENZE_INFERMIERE else COMPETENZE
        return elenco.firstOrNull { (re, _) -> re.containsMatchIn(t) }?.second
    }

    fun estrai(testo: String, oggi: LocalDate): CriteriFarmacista {
        val t = IntentClassifier.normalizza(testo)
        val professione = professioneMenzionata(testo)
        // Un periodo vince sul giorno: in "dal 3 al 10 novembre" il parser leggerebbe solo il 3.
        val periodo = EstrattorePeriodo.estrai(testo, oggi)
        // Il luogo si cerca senza periodo e senza "mia zona": il parser prenderebbe "Rho a novembre",
        // "Per" o "Me" come nome del comune.
        val inZona = MIA_ZONA.containsMatchIn(testo)
        val soloLuogo = EstrattorePeriodo.senzaPeriodo(testo).replace(MIA_ZONA, " ")
        return CriteriFarmacista(
            professione = professione,
            competenza = competenzaMenzionata(testo, professione),
            tariffaMax = VoiceCommandParser.compensoMenzionato(testo),
            giorno = if (periodo != null) null else VoiceCommandParser.dataMenzionata(testo, oggi),
            comune = VoiceCommandParser.comuneMenzionato(soloLuogo),
            provincia = VoiceCommandParser.provinciaMenzionata(soloLuogo),
            periodo = periodo,
            inZona = inZona
        )
    }

    /**
     * Candidati in ordine: prima chi è disponibile quel giorno entro la tariffa, poi i preferiti,
     * la tariffa più bassa e chi ha più turni completati. Se è stato chiesto un giorno, chi non ha
     * disponibilità pubblicata per quel giorno resta in fondo ("disponibilità da chiedere").
     * Chi chiede più della tariffa massima viene escluso.
     */
    fun ordina(
        farmacisti: List<Professionista>,
        annunci: List<AnnuncioTurno>,
        criteri: CriteriFarmacista,
        preferiti: Set<String>
    ): List<CandidatoFarmacista> {
        // Solo le disponibilità della professione cercata: farmacisti e infermieri hanno id che possono coincidere.
        val disponibilita = annunci.filter {
            it.tipo == AnnuncioTipo.CERCO_LAVORO && it.stato == AnnuncioStato.APERTO && it.creatoDa.name == criteri.professione.name &&
                criteri.accetta(it.dataTurno.toLocalDateTime(TimeZone.UTC).date)
        }.groupBy { it.creatoreId }

        return farmacisti.mapNotNull { f ->
            val id = f.id ?: return@mapNotNull null
            // La disponibilità più economica di quel farmacista (entro la tariffa, se chiesta).
            val entroTariffa = disponibilita[id].orEmpty()
                .filter { a -> criteri.tariffaMax == null || (a.compenso ?: 0.0) <= criteri.tariffaMax }
            val sua = entroTariffa.minByOrNull { it.compenso ?: Double.MAX_VALUE }
            // Ha disponibilità ma tutte oltre la tariffa: escluso.
            if (sua == null && criteri.tariffaMax != null && disponibilita[id].orEmpty().isNotEmpty()) return@mapNotNull null
            val giorni = entroTariffa.map { it.dataTurno.toLocalDateTime(TimeZone.UTC).date }.distinct().size
            CandidatoFarmacista(f, sua, id in preferiti, giorni)
        }.sortedWith(
            compareByDescending<CandidatoFarmacista> { it.disponibilita != null }
                // In un periodo, prima chi copre più giorni.
            .thenByDescending { it.giorniDisponibili }
            .thenByDescending { it.preferito }
                .thenBy { it.tariffa ?: Double.MAX_VALUE }
                .thenByDescending { it.farmacista.storicoCompletati - it.farmacista.storicoAnnullati }
        )
    }

    /** "Laura Rossi — disponibile il 27/9, 08:00-20:00, 30 €/h, galenica · preferita" */
    fun descrivi(c: CandidatoFarmacista): String = buildString {
        append("${c.farmacista.nome} ${c.farmacista.cognome}")
        val d = c.disponibilita
        if (d != null) {
            val g = d.dataTurno.toLocalDateTime(TimeZone.UTC).date
            append(" — disponibile il ${g.dayOfMonth}/${g.monthNumber}, ${d.oraInizio}-${d.oraFine}")
            d.compenso?.let { append(", ${if (it % 1.0 == 0.0) it.toInt().toString() else it.toString().replace('.', ',')} €/h") }
            if (c.giorniDisponibili > 1) append(" (e altri ${c.giorniDisponibili - 1} giorni nel periodo)")
        } else {
            append(" — disponibilità da chiedere")
        }
        if (c.preferito) append(" · preferito")
    }
}
